import React, { useEffect, useState } from "react";
import { Box, Typography, Switch, Stack, Button } from "@mui/material";
import { allWidgets } from "../../components/widgets/widgetsConfig";
import { SideBar } from "../../components/shared/sideBar/sideBar";
import { useAppTheme } from "../../components/themes/appThemeProvider";
import sideBarStyles from "../../components/shared/sideBar/sideBar.module.css";

export const CustomViewSideBar = ({
  selectedWidgets,
  editMode,
  setEditMode,
  setSelectedWidgets,
  onReset = () => {},
}) => {
  const { mode, toggleTheme } = useAppTheme();
  const isDarkMode = mode === "dark";

  const onToggleEditMode = () => setEditMode((prev) => !prev);
  const toggleWidget = (id) => {
    const isSelected = selectedWidgets.some((w) => w.id === id);
    if (isSelected) {
      setSelectedWidgets(selectedWidgets.filter((w) => w.id !== id));
    } else {
      const widgetToAdd = allWidgets.find((w) => w.id === id);
      if (widgetToAdd) setSelectedWidgets([...selectedWidgets, widgetToAdd]);
    }
  };

  return (
    <SideBar>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          padding: 0,
          gap: 2,
        }}
      >
        <Box
          sx={{
            borderBottom: `1px solid ${isDarkMode ? "#333" : "#ccc"}`,
            paddingBottom: "20px",
            marginBottom: "0px",
          }}
        >
          <Typography variant="subtitle2" className={sideBarStyles.sectionTitle} sx={{ mb: 1 }}>
            Edit Dashboard
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: isDarkMode ? "#333" : "#f0f0f0",
              borderRadius: "6px",
              px: 1.5,
              py: 0.75,
            }}
          >
            <Typography variant="body2">Edit Mode</Typography>
            <Switch checked={editMode} onChange={onToggleEditMode} size="small" />
          </Box>
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography variant="subtitle2" className={sideBarStyles.sectionTitle}>
            Select Widgets
          </Typography>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
            }}
          >
            {allWidgets.map((widget) => (
              <Box
                key={widget.id}
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  border: `1px solid ${isDarkMode ? "#444" : "#ccc"}`,
                  borderRadius: "8px",
                  backgroundColor: isDarkMode ? "#333" : "#f0f0f0",
                  boxShadow: isDarkMode
                    ? "0 1px 2px rgba(0,0,0,0.3)"
                    : "0 1px 2px rgba(0,0,0,0.08)",
                  "&:hover": {
                    boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1,
                    mb: 1,
                    borderBottom: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {widget.title}
                  </Typography>
                  <Switch
                    checked={selectedWidgets.some((w) => w.id === widget.id)}
                    onChange={() => toggleWidget(widget.id)}
                    size="small"
                    sx={{
                      "& .MuiSwitch-thumb": {
                        backgroundColor: selectedWidgets.includes(widget.id) ? "#fff" : "#ccc",
                        height: 18,
                        width: 18,
                        mt: "2px",
                      },
                      "& .MuiSwitch-track": {
                        backgroundColor: selectedWidgets.includes(widget.id) ? "#ccc" : "#C14D41",
                        opacity: 1,
                        borderRadius: "10px",
                        height: 18,
                      },
                    }}
                  />
                </Box>

                {widget.description && (
                  <Typography variant="caption" sx={{ color: isDarkMode ? "#aaa" : "#666" }}>
                    {widget.description}
                  </Typography>
                )}

                {widget.preview && (
                  <Box
                    sx={{
                      mt: 1,
                      height: 80,
                      borderRadius: "6px",
                      overflow: "hidden",
                      backgroundImage: `url(${widget.preview})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      border: "1px solid #111",
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={onReset}
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              textTransform: "none",
              borderRadius: "8px",
              px: 2.5,
              py: 0.75,
              color: isDarkMode ? "#ccc" : "#333",
              borderColor: isDarkMode ? "#555" : "#ccc",
              backgroundColor: isDarkMode ? "#2a2a2a" : "#f9f9f9",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: isDarkMode ? "#444" : "#eaeaea",
                borderColor: isDarkMode ? "#777" : "#aaa",
              },
            }}
          >
            Reset Layout
          </Button>
        </Stack>
      </Box>
    </SideBar>
  );
};
